import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  isDark: boolean;
  className?: string;
  style?: React.CSSProperties;
  options: { value: string; label: string }[];
  placeholder?: string;
  placement?: 'top' | 'bottom';
  disabledValues?: string[];
  tooltipMap?: Record<string, string>;
  disabled?: boolean;
  noOpacity?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  searchable?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  isDark,
  className = "",
  style = {},
  options,
  placeholder = "Select an option",
  placement = "bottom",
  disabledValues = [],
  tooltipMap = {},
  disabled = false,
  noOpacity = false,
  onFocus,
  onBlur,
  searchable
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<{ left: number; top: number; width: number; placement: string } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-enable search if there are more than 5 options, unless searchable is explicitly passed
  const isSearchEnabled = searchable !== undefined ? searchable : options.length > 5;

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    } else {
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 240; // max-h-60 = 240px
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      let top = shouldOpenUp
        ? rect.top + window.scrollY - 2
        : rect.bottom + window.scrollY + 2;

      setDropdownPosition({
        left: rect.left,
        top: top,
        width: rect.width,
        placement: shouldOpenUp ? "top" : "bottom"
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const selectedOption = options.find(option => option.value === value);

  // Filter options based on query
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <style>{`
        .custom-dropdown-scroll::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .custom-dropdown-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-dropdown-scroll::-webkit-scrollbar-thumb {
          background: ${isDark ? '#2E2F33' : '#E5E7EB'};
          border-radius: 10px;
        }
        .custom-dropdown-scroll::-webkit-scrollbar-thumb:hover {
          background: #B59143;
        }
      `}</style>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        title={selectedOption ? selectedOption.label : placeholder}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        className={`px-3 py-2 text-sm rounded-lg transition-all duration-200 text-left outline-none flex items-center justify-between shadow-sm border font-medium ${isDark
          ? 'bg-[#14161B] text-white border-[#2E2F33] hover:border-[#B59143] focus:border-[#B59143] focus:ring-1 focus:ring-[#B59143]'
          : 'bg-white text-gray-900 border-gray-200 hover:border-[#B59143] focus:border-[#B59143] focus:ring-1 focus:ring-[#B59143]'
          } ${className} ${disabled ? 'cursor-not-allowed' : ''} ${disabled && !noOpacity ? 'opacity-60' : ''}`}
        style={style}
      >
        <span className={`${!selectedOption ? (isDark ? 'text-gray-400' : 'text-gray-500') : ''} truncate flex-1`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 ml-1 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && dropdownPosition && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className={`${isDark
            ? 'bg-[#14161B] border-[#2E2F33] text-white'
            : 'bg-white border-gray-200 text-gray-900'
            } rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto custom-dropdown-scroll animate-in fade-in zoom-in-95 duration-150 flex flex-col`}
          style={{
            position: "absolute",
            left: dropdownPosition.left,
            top: dropdownPosition.top,
            width: dropdownPosition.width,
            border: `1px solid ${isDark ? '#2E2F33' : '#E5E7EB'}`,
            zIndex: 9999,
            transform: dropdownPosition.placement === 'top' ? 'translateY(-100%)' : 'none',
          }}
        >
          {isSearchEnabled && (
            <div className={`px-2 py-1.5 border-b sticky top-0 z-10 ${isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white border-gray-100'}`}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className={`w-full px-2.5 py-1.5 text-sm rounded outline-none border transition-all ${isDark
                  ? 'bg-[#0B0B0F] border-[#2E2F33] text-white focus:border-[#B59143]'
                  : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#B59143]'
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
              />
            </div>
          )}
          
          <div className="overflow-y-auto flex-1 custom-dropdown-scroll">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-center text-gray-500 italic">
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isDisabled = disabledValues.includes(option.value);
                const isSelected = value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={isDisabled}
                    title={isDisabled ? (tooltipMap[option.value] || option.label) : option.label}
                    className={`w-full px-4 py-2 text-sm text-left transition-colors flex items-center justify-between ${isDark
                      ? 'text-gray-200 hover:bg-[#2A3441] hover:text-white'
                      : 'text-gray-700 hover:bg-[#FFF7E6] hover:text-gray-900'
                      } ${isSelected ? (isDark ? 'bg-[#2A3441] text-[#FBD282] font-semibold' : 'bg-[#FFF7E6] text-[#B59143] font-semibold') : ''} ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    onClick={(e) => {
                      if (isDisabled) return;
                      e.preventDefault();
                      e.stopPropagation();
                      onChange(option.value);
                      setIsOpen(false);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                  >
                    <span className="truncate mr-2">{option.label}</span>
                    {isSelected && (
                      <svg className="w-4 h-4 text-[#B59143] ml-auto flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CustomDropdown;
